import { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Phone, UploadCloud, FileText, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Reveal from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { COMPANY } from "@/constants/company";

const ENDPOINT = "/send-contact.php";
const MAX_MB = 10;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";

const CONSENT_1 =
  "Ho letto l'informativa privacy e acconsento al trattamento dei miei dati per ricevere la valutazione richiesta.";
const CONSENT_2 =
  "Acconsento a ricevere comunicazioni commerciali di Rossi Energia — nuove offerte, proposte e promemoria di scadenza del contratto — via email, WhatsApp, SMS o telefono. Posso revocare il consenso in qualsiasi momento.";

const isoWithTz = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const oh = p(Math.floor(Math.abs(off) / 60));
  const om = p(Math.abs(off) % 60);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}${sign}${oh}:${om}`;
};

export default function Contact() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefono: "",
    tipologia: "privati",
    messaggio: "",
  });
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const inputRef = useRef(null);

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Il file supera i ${MAX_MB}MB.`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // prevent double submit
    if (!form.nome || !form.email) {
      toast.error("Inserisci nome ed email.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) {
      toast.error("Inserisci un indirizzo email valido.");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare l'informativa privacy per inviare la richiesta.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("consenso_valutazione", privacy ? "sì" : "no");
      data.append("consenso_marketing", marketing ? "sì" : "no");
      data.append("consenso_data_ora", isoWithTz(new Date()));
      data.append("consenso_testo_1", CONSENT_1);
      data.append("consenso_testo_2", CONSENT_2);
      data.append("website", honeypot); // honeypot
      if (file) data.append("bolletta", file);
      const res = await axios.post(ENDPOINT, data);
      if (res?.data?.status === "success") {
        toast.success(res.data.message || "Richiesta inviata! Ti ricontatteremo al più presto.");
        setForm({ nome: "", email: "", telefono: "", tipologia: "privati", messaggio: "" });
        setFile(null);
        setPrivacy(false);
        setMarketing(false);
      } else {
        toast.error(res?.data?.message || "Errore durante l'invio. Riprova.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Errore durante l'invio. Riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 bg-sage-100 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="rounded-3xl bg-white border border-sage-200 shadow-sm overflow-hidden grid lg:grid-cols-5">
            {/* Info */}
            <div className="lg:col-span-2 bg-forest-900 text-sage-50 p-8 lg:p-12 flex flex-col">
              <span className="text-xs uppercase tracking-[0.2em] font-medium text-sage-300">
                Valutazione senza impegno
              </span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
                Scopri quanto puoi risparmiare.
              </h2>
              <p className="mt-4 text-base font-light text-sage-200/80 leading-relaxed">
                Carica la tua bolletta: la analizziamo gratuitamente e senza
                impegno, e ti proponiamo l'offerta più adatta a te.
              </p>

              <div className="mt-auto pt-10 space-y-5">
                <a
                  href={COMPANY.phoneTel}
                  data-testid="contact-phone"
                  className="flex items-start gap-3 group"
                >
                  <Phone className="h-5 w-5 text-sage-300 mt-0.5" strokeWidth={1.7} />
                  <span className="text-sage-100 group-hover:text-white transition-colors">
                    {COMPANY.phone}
                  </span>
                </a>
                <a
                  href={COMPANY.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="flex items-start gap-3 group"
                >
                  <WhatsAppIcon className="h-5 w-5 text-sage-300 mt-0.5" />
                  <span className="text-sage-100 group-hover:text-white transition-colors">
                    Scrivici su WhatsApp
                  </span>
                </a>
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-email"
                  className="flex items-start gap-3 group"
                >
                  <Mail className="h-5 w-5 text-sage-300 mt-0.5" strokeWidth={1.7} />
                  <span className="text-sage-100 group-hover:text-white transition-colors">
                    {COMPANY.email}
                  </span>
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-sage-300 mt-0.5" strokeWidth={1.7} />
                  <span className="text-sage-100">{COMPANY.address}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-sage-300 mt-0.5" strokeWidth={1.7} />
                  <span className="text-sage-100">{COMPANY.orari}</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="lg:col-span-3 p-8 lg:p-12 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-forest-800">Nome *</label>
                  <Input
                    data-testid="input-nome"
                    value={form.nome}
                    onChange={setField("nome")}
                    placeholder="Il tuo nome"
                    className="mt-2 rounded-xl border-sage-200 focus-visible:ring-sage-400 bg-sage-50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-forest-800">Email *</label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    placeholder="nome@email.it"
                    className="mt-2 rounded-xl border-sage-200 focus-visible:ring-sage-400 bg-sage-50"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-forest-800">Telefono</label>
                  <Input
                    data-testid="input-telefono"
                    value={form.telefono}
                    onChange={setField("telefono")}
                    placeholder="+39 ..."
                    className="mt-2 rounded-xl border-sage-200 focus-visible:ring-sage-400 bg-sage-50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-forest-800">Tipologia</label>
                  <div className="mt-2 flex rounded-xl bg-sage-100 p-1">
                    {["privati", "business"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-testid={`tipologia-${t}`}
                        onClick={() => setForm((s) => ({ ...s, tipologia: t }))}
                        className={`flex-1 rounded-lg py-2 text-sm font-medium capitalize transition-colors ${
                          form.tipologia === t
                            ? "bg-forest-900 text-sage-50"
                            : "text-forest-700 hover:text-forest-900"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-forest-800">Messaggio</label>
                <Textarea
                  data-testid="input-messaggio"
                  value={form.messaggio}
                  onChange={setField("messaggio")}
                  placeholder="Raccontaci le tue esigenze (facoltativo)"
                  rows={3}
                  className="mt-2 rounded-xl border-sage-200 focus-visible:ring-sage-400 bg-sage-50 resize-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-forest-800">La tua bolletta</label>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  data-testid="upload-bolletta-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                />
                {!file ? (
                  <motion.div
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    data-testid="upload-dropzone"
                    className={`mt-2 cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
                      dragging
                        ? "border-sage-400 bg-sage-100"
                        : "border-sage-300 bg-sage-50 hover:border-sage-400"
                    }`}
                  >
                    <UploadCloud className="mx-auto h-8 w-8 text-sage-500" strokeWidth={1.5} />
                    <p className="mt-3 text-sm font-medium text-forest-800">
                      Trascina qui la bolletta o clicca per caricare
                    </p>
                    <p className="mt-1 text-sm text-forest-700/70">
                      PDF, JPG, PNG o WebP · max {MAX_MB} MB
                    </p>
                  </motion.div>
                ) : (
                  <div
                    data-testid="upload-file-preview"
                    className="mt-2 flex items-center justify-between rounded-xl border border-sage-200 bg-sage-50 px-4 py-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-5 w-5 text-sage-600 shrink-0" strokeWidth={1.7} />
                      <span className="truncate text-sm text-forest-800">{file.name}</span>
                    </div>
                    <button
                      type="button"
                      data-testid="upload-remove"
                      onClick={() => setFile(null)}
                      className="rounded-full p-1 text-forest-700 hover:bg-sage-200 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <p data-testid="bolletta-notice" className="mt-2 text-sm text-forest-700/80 leading-relaxed">
                  Se puoi, oscura l'IBAN e i dati di pagamento prima di caricarla:
                  per la valutazione non ci servono. Caricando la bolletta dichiari
                  di esserne l'intestatario o di essere autorizzato dall'intestatario
                  a trasmetterla a Rossi Energia.
                </p>
              </div>

              {/* Honeypot anti-spam (invisible to users) */}
              <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
                <label>
                  Non compilare questo campo
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={privacy}
                  onCheckedChange={(v) => setPrivacy(v === true)}
                  data-testid="consenso-valutazione-checkbox"
                  className="mt-0.5 border-sage-300 data-[state=checked]:bg-forest-900 data-[state=checked]:border-forest-900"
                />
                <span
                  onClick={() => setPrivacy((v) => !v)}
                  className="text-sm font-light text-forest-700 leading-relaxed cursor-pointer"
                >
                  Ho letto l'
                  <a
                    href="/privacy/"
                    target="_blank"
                    rel="noopener"
                    data-testid="privacy-link"
                    onClick={(e) => e.stopPropagation()}
                    className="underline hover:text-forest-900"
                  >
                    informativa privacy
                  </a>{" "}
                  e acconsento al trattamento dei miei dati per ricevere la
                  valutazione richiesta. *
                </span>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  checked={marketing}
                  onCheckedChange={(v) => setMarketing(v === true)}
                  data-testid="consenso-marketing-checkbox"
                  className="mt-0.5 border-sage-300 data-[state=checked]:bg-forest-900 data-[state=checked]:border-forest-900"
                />
                <span
                  onClick={() => setMarketing((v) => !v)}
                  className="text-sm font-light text-forest-700 leading-relaxed cursor-pointer"
                >
                  Acconsento a ricevere comunicazioni commerciali di Rossi Energia
                  — nuove offerte, proposte e promemoria di scadenza del contratto —
                  via email, WhatsApp, SMS o telefono. Posso revocare il consenso in
                  qualsiasi momento.
                </span>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-contact-form"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest-900 px-7 py-4 text-sm font-medium text-sage-50 hover:bg-forest-700 transition-[background-color,transform] duration-300 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Invio in corso..." : "Richiedi valutazione gratuita"}
              </button>
            </form>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
